import React, { useRef, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { StylishConfirmDialog } from 'components/DesignSystems/New/StylishConfirmDialog';
import isRoleReadOnly from 'PREPAREsrc/utils/isRoleReadOnly';
import {
  FileOrFolder,
  FileScopeType,
  filesOrFoldersKeys,
} from './hooks/useGetAllFiles';
import { useDeleteFolder } from './hooks/folderMutations';
import { useQueryClient } from '@tanstack/react-query';
import AddEditFolderDialog from './AddEditFolderDialog';
import { useUploadFiles } from './hooks/fileMutations';
import { toast } from 'react-toastify';
import { getAcceptString } from '../DisasterGPT/DisasterGPTConstants';

interface Props {
  folder: FileOrFolder;
  userWorkspaceRoleName?: string;
  onFolderAction?: (actionType: string, folder: FileOrFolder) => void;
  onEditFolder?: (folder: FileOrFolder) => void;
  ownerId: string;
  scopeType?: FileScopeType;
}

const FolderActionsMenu: React.FC<Props> = ({
  folder,
  userWorkspaceRoleName,
  onFolderAction,
  onEditFolder,
  ownerId,
  scopeType,
}) => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editFolder, setEditFolder] = useState<FileOrFolder | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { mutateAsync: uploadFiles } = useUploadFiles();

  const queryClient = useQueryClient();
  const { mutate: deleteFolder } = useDeleteFolder(() => {
    queryClient.invalidateQueries({
      queryKey: filesOrFoldersKeys.allFiles(ownerId),
    });
  });

  const onEditFolderClick = () => {
    if (onEditFolder) {
      onEditFolder(folder);
    }
    if (onFolderAction) onFolderAction('edit', folder);
    setEditFolder(folder);
  };

  const onConfirmDelete = () => {
    if (deleteId) {
      deleteFolder({ folderId: deleteId, workspaceId: ownerId });
      setDeleteId(null);
      if (onFolderAction) onFolderAction('delete', folder);
    }
  };

  const onUploadClick = () => fileInputRef.current?.click();

  const changeHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length <= 0) return;
    if (files.length > 10) {
      toast.info('Maximum upload limit is 10 files at a time');
      return;
    }

    try {
      // Use consolidated upload API - handles optimistic updates and cache invalidation
      await uploadFiles({
        files: Array.from(files),
        notefolderId: folder.id,
        workspaceId: ownerId,
      });

      // Clear input and trigger callback
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onFolderAction) onFolderAction('upload', folder);
    } catch (e: any) {
      // Error toast is handled by useUploadFiles hook
      console.error('Upload error:', e);
    }
  };

  return (
    <>
      <Dropdown className="flex-shrink-0">
        <Dropdown.Toggle className="button--icon">
          <i className="fa fa-ellipsis-v" aria-hidden="true"></i>
        </Dropdown.Toggle>
        <Dropdown.Menu align="end">
          {!isRoleReadOnly(userWorkspaceRoleName) && (
            <>
              <Dropdown.Item onClick={onUploadClick}>
                <SharedIcon iconName="upload" />
                Upload Files
              </Dropdown.Item>
              <Dropdown.Item onClick={onEditFolderClick}>
                <SharedIcon iconName="stylus" />
                Edit Folder
              </Dropdown.Item>
            </>
          )}
          {!isRoleReadOnly(userWorkspaceRoleName) && (
            <Dropdown.Item onClick={() => setDeleteId(folder.id)}>
              <SharedIcon iconName="delete" />
              Delete Folder
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {/* Hidden file input for Upload Files action */}
      {!isRoleReadOnly(userWorkspaceRoleName) && (
        <input
          ref={fileInputRef}
          type="file"
          name="file"
          multiple
          accept={getAcceptString()}
          style={{ display: 'none' }}
          onChange={changeHandler}
        />
      )}

      {deleteId && (
        <StylishConfirmDialog
          show={!!deleteId}
          onClose={() => setDeleteId(null)}
          dialogTitle={'Delete Folder'}
          dialogContent={'Are you sure you want to do this?'}
          onConfirm={onConfirmDelete}
        />
      )}

      {editFolder && (
        <AddEditFolderDialog
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: filesOrFoldersKeys.allFiles(ownerId),
            });
            setEditFolder(null);
          }}
          onClose={() => setEditFolder(null)}
          dialogType={'Edit'}
          ownerId={ownerId}
          folderToEdit={folder}
          scopeType={scopeType}
        />
      )}
    </>
  );
};

export default FolderActionsMenu;
